/**
 * Adapter between the typed AI-suggestion sidecar ROWS (the source of truth,
 * synced into Dexie as `preOrderSuggestions` / `preOrderFlowSuggestions`) and
 * the in-memory `AiSuggestionBlock` shape the recap render + resolve paths
 * already understand.
 *
 * Why an adapter rather than a rewrite: ~15 display readers
 * (`recapService` helpers, the cells/badges/banners, `aiAcceptVisibility`)
 * and the entire `flow.ts` / `header.ts` merge math read the legacy block
 * shape. Projecting rows → blocks keeps every one of them unchanged; only
 * the read source (rows, not embedded JSON) and the resolve side-effect
 * (stamp the row's `accepted_at` / `declined_at`) move.
 *
 * Row → block mapping (per the `SuggestionBase` contract):
 *  - `fromValues` KEYS are the changed-field mask AND hold the `from`
 *    snapshot at proposal time. Keys are camelCase (`quantity`, `comment`,
 *    the metadata blob sentinel `flowMetadata` / `preOrderMetadata`).
 *  - The mirrored columns on the row (`quantity`, `weight`, `comment`, …)
 *    hold the proposed `to` value; `metadataPatch` holds the metadata
 *    blob `to`.
 *  - `operation === SUGGESTION_OP_DELETE` → a `type:delete` block (no
 *    `changes`). `SUGGESTION_OP_MODIFY` → a `type:modify` block whose
 *    `changes` pairs each masked field's `from` with its mirrored `to`.
 *
 * There is no `type:add` row: AI creates land in the MAIN table at
 * `flow.status = AI_PROPOSAL` (5) with no sidecar row. The projection
 * synthesizes a `type:add` block for those from status alone.
 */

import type { PreOrderSuggestion } from "@stockline/shared/types/ai/pre-order-suggestion";
import type { PreOrderFlowSuggestion } from "@stockline/shared/types/ai/pre-order-flow-suggestion";
import { SUGGESTION_OP_DELETE } from "@stockline/shared/types/ai/suggestion-base";
import {
  type AiFieldChange,
  type AiSuggestionBlock,
  FLOW_METADATA_BLOB_FIELD,
  HEADER_METADATA_BLOB_FIELD,
} from "./helpers";

/** The camelCase header columns a `PreOrderSuggestion` row mirrors as `to`. */
const HEADER_MIRRORED_FIELDS = [
  "partnerId",
  "comment",
  "deliveryDate",
  "orderDate",
] as const;

/** The camelCase flow columns a `PreOrderFlowSuggestion` row mirrors as `to`. */
const FLOW_MIRRORED_FIELDS = [
  "productId",
  "partnerId",
  "unitId",
  "quantity",
  "weight",
  "pricePerWeight",
  "comment",
] as const;

/**
 * A block paired with the sidecar row id it was projected from, so the
 * resolver can stamp the right row after the push-batch lands. A synthetic
 * add block (AI-created flow, no row) carries `rowId === null`.
 */
export interface AdaptedSuggestion {
  block: AiSuggestionBlock;
  /**
   * Every pending sidecar row this block was merged from — the resolver stamps
   * ALL of them. Multiple pending rows per target are intentional (a re-propose
   * on an unresolved row, or several inbound comms hitting the same line before
   * the user resolves); they merge field-by-field, the later row winning on a
   * conflicting field. A synthetic add block (AI-created flow, no row) → `[]`.
   */
  rowIds: string[];
}

/** Synthetic `index` for projected blocks — targets collapse to one merged
 * block, so the index is constant. */
const PROJECTED_INDEX = 1;

function isPendingRow(
  row: PreOrderSuggestion | PreOrderFlowSuggestion,
): boolean {
  return (
    row.acceptedAt == null && row.declinedAt == null && row.deletedAt == null
  );
}

/**
 * Assemble a block's `changes` from a row's `fromValues` mask + the row's
 * mirrored `to` columns + `metadataPatch`. Only fields present in
 * `fromValues` (the mask) become entries — EXCEPT the metadata blob, which is
 * surfaced whenever the row carries a `metadataPatch` (see below).
 */
function buildChanges(
  fromValues: Record<string, unknown> | null,
  mirroredTo: Record<string, unknown>,
  metadataBlobField: string,
  columnPatch: Record<string, unknown> | null,
  metadataPatch: Record<string, unknown> | null,
): Record<string, AiFieldChange> {
  const from = fromValues ?? {};
  const changes: Record<string, AiFieldChange> = {};
  for (const field of Object.keys(from)) {
    if (field === metadataBlobField) {
      changes[field] = {
        from: from[field] ?? null,
        to: metadataPatch ?? null,
      };
      continue;
    }
    // Own-property check, not `in`: the masked field set comes from
    // `fromValues` keys, and a key like `constructor` must not match
    // `Object.prototype` and emit a bogus change.
    if (Object.hasOwn(mirroredTo, field)) {
      changes[field] = { from: from[field] ?? null, to: mirroredTo[field] };
      continue;
    }
    // Non-mirrored column field (hiddenComment, employeeId, …): its proposed
    // `to` rides the row's columnPatch overflow carrier.
    if (columnPatch && Object.hasOwn(columnPatch, field)) {
      changes[field] = { from: from[field] ?? null, to: columnPatch[field] };
    }
  }
  // The backend masks `fromValues` with the COLUMNS it diffed, never the
  // metadata blob: a quantity change staged from `update_pre_order` records
  // `{quantity: 18}` and hangs the resolved `delineation` off `metadataPatch`.
  // Keying the blob entry off the mask would therefore drop it — silently, and
  // in both directions: the pre-accept hover overlay reads
  // `changes[blob].to`, and `assembleResolvedMetadata` reads the same entry on
  // accept. Surface the blob whenever the row carries one (STK-1571).
  if (metadataPatch && !Object.hasOwn(from, metadataBlobField)) {
    changes[metadataBlobField] = { from: null, to: metadataPatch };
  }
  return changes;
}

function pickMirrored(
  row: PreOrderSuggestion | PreOrderFlowSuggestion,
  fields: ReadonlyArray<string>,
): Record<string, unknown> {
  const indexed = row as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    // Keep the field whenever the row carries the column at all — including a
    // `null` proposed value. A `null` mirrored column is an AI proposal to
    // CLEAR a nullable field (comment, weight, pricePerWeight, deliveryDate,
    // orderDate); dropping it here would make `buildChanges` skip the field, so
    // the clear would be invisible in the diff and silently lost on accept.
    if (f in indexed) out[f] = indexed[f];
  }
  return out;
}

function baseBlock(
  row: PreOrderSuggestion | PreOrderFlowSuggestion,
  type: AiSuggestionBlock["type"],
): AiSuggestionBlock {
  return {
    index: PROJECTED_INDEX,
    status: "pending",
    type,
    batchId: row.batchId ?? "",
    proposedBy: row.agentExecutionId ?? "",
    proposedAt: row.proposedAt,
  };
}

/** Adapt a pending header suggestion row into a block + its row id. */
export function adaptHeaderSuggestion(
  row: PreOrderSuggestion,
): AdaptedSuggestion {
  if (row.operation === SUGGESTION_OP_DELETE) {
    return { block: baseBlock(row, "delete"), rowIds: [row.id] };
  }
  const block = baseBlock(row, "modify");
  block.changes = buildChanges(
    row.fromValues,
    pickMirrored(row, HEADER_MIRRORED_FIELDS),
    HEADER_METADATA_BLOB_FIELD,
    row.columnPatch,
    row.metadataPatch,
  );
  return { block, rowIds: [row.id] };
}

/** Adapt a pending flow suggestion row into a block + its row id. */
export function adaptFlowSuggestion(
  row: PreOrderFlowSuggestion,
): AdaptedSuggestion {
  if (row.operation === SUGGESTION_OP_DELETE) {
    return { block: baseBlock(row, "delete"), rowIds: [row.id] };
  }
  const block = baseBlock(row, "modify");
  block.changes = buildChanges(
    row.fromValues,
    pickMirrored(row, FLOW_MIRRORED_FIELDS),
    FLOW_METADATA_BLOB_FIELD,
    row.columnPatch,
    row.metadataPatch,
  );
  return { block, rowIds: [row.id] };
}

/** Synthetic `type:add` block for an AI-created flow (status=5, no row). */
export function syntheticAddBlock(): AiSuggestionBlock {
  return {
    index: PROJECTED_INDEX,
    status: "pending",
    type: "add",
    batchId: "",
    proposedBy: "",
    proposedAt: new Date(0).toISOString(),
  };
}

/**
 * Merge all pending suggestions targeting one committed row into a single
 * block. Rows arrive `syncId`-ascending, so a later row wins on a conflicting
 * field while a field only one row touches is kept (union, last-write-wins).
 * The `from` snapshot is preserved from the FIRST row that touched a field (the
 * committed value the user should see), the `to` taken from the LAST. A
 * `delete` supersedes prior field changes; a later modify supersedes a delete.
 * Every merged row's id is collected so the resolver stamps them all — no
 * pending row is ever silently dropped.
 */
function mergeAdaptedSuggestions(
  list: readonly AdaptedSuggestion[],
): AdaptedSuggestion {
  const rowIds: string[] = [];
  let last = list[0]!.block;
  let type: AiSuggestionBlock["type"] = "modify";
  const changes: Record<string, AiFieldChange> = {};
  for (const adapted of list) {
    rowIds.push(...adapted.rowIds);
    last = adapted.block;
    if (adapted.block.type === "delete") {
      type = "delete";
      for (const key of Object.keys(changes)) delete changes[key];
      continue;
    }
    type = "modify";
    for (const [field, change] of Object.entries(adapted.block.changes ?? {})) {
      const prior = changes[field];
      // Keep the committed `from` from the first row; take the latest `to`.
      changes[field] = prior ? { ...prior, to: change.to } : { ...change };
    }
  }
  const block: AiSuggestionBlock = { ...last, type };
  if (type === "delete") delete block.changes;
  else block.changes = changes;
  return { block, rowIds };
}

/**
 * targetId → one merged adapted suggestion covering EVERY pending row for that
 * target. Multiple pending rows per target are intentional; they merge
 * field-by-field (last-write-wins) rather than collapsing to the last row, so
 * no proposed field and no row-stamp is lost at resolution.
 */
export function buildHeaderSuggestionMap(
  rows: PreOrderSuggestion[],
): Map<string, AdaptedSuggestion> {
  const byTarget = new Map<string, AdaptedSuggestion[]>();
  for (const row of rows) {
    if (!isPendingRow(row)) continue;
    const adapted = adaptHeaderSuggestion(row);
    const list = byTarget.get(row.targetId);
    if (list) list.push(adapted);
    else byTarget.set(row.targetId, [adapted]);
  }
  const map = new Map<string, AdaptedSuggestion>();
  for (const [targetId, list] of byTarget) {
    map.set(targetId, mergeAdaptedSuggestions(list));
  }
  return map;
}

export function buildFlowSuggestionMap(
  rows: PreOrderFlowSuggestion[],
): Map<string, AdaptedSuggestion> {
  const byTarget = new Map<string, AdaptedSuggestion[]>();
  for (const row of rows) {
    if (!isPendingRow(row)) continue;
    const adapted = adaptFlowSuggestion(row);
    const list = byTarget.get(row.targetId);
    if (list) list.push(adapted);
    else byTarget.set(row.targetId, [adapted]);
  }
  const map = new Map<string, AdaptedSuggestion>();
  for (const [targetId, list] of byTarget) {
    map.set(targetId, mergeAdaptedSuggestions(list));
  }
  return map;
}
